import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Box,
  Tooltip,
  Typography,
  Fab,
  alpha,
} from '@mui/material';

import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import DashboardIcon from '@mui/icons-material/SpaceDashboard';
import DevicesIcon from '@mui/icons-material/Devices';
import NotificationsIcon from '@mui/icons-material/NotificationsActive';
import SupportIcon from '@mui/icons-material/SupportAgent';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FeedbackIcon from '@mui/icons-material/RateReview';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import HowToRegIcon from '@mui/icons-material/HowToReg';

import LogoutConfirmation from '../../components/Modals/LogoutConfirmation';
import { usePendingApprovalsCount } from '../../hooks/usePendingApprovalsCount';
const SIDEBAR_WIDTH_EXPANDED = 260;
const SIDEBAR_WIDTH_COLLAPSED = 76;

const BRAND_PRIMARY = '#1e40af';
const BRAND_LIGHT = '#eff6ff';

const buildNavSections = (myApprovalsItem) => {
  const sections = [
    {
      title: 'Main',
      items: [
        {
          path: '/user/dashboard',
          key: 'dashboard',
          label: 'Overview',
          icon: <DashboardIcon />,
        },
      ],
    },
    {
      title: 'Assets',
      items: [
        {
          path: '/user/assignedassets',
          key: 'assignedassets',
          label: 'My Assets',
          icon: <DevicesIcon />,
        },
      ],
    },
    
    {
      title: 'Support',
      items: [
        {
          path: '/user/tickets',
          key: 'tickets',
          label: 'Support Tickets',
          icon: <SupportIcon />,
        },
        {
          path: '/user/help-center',
          key: 'helpCenter',
          label: 'Help',
          icon: <MenuBookIcon />,
        },
        {
          path: '/user/feedback',
          key: 'feedback',
          label: 'Submit Feedback',
          icon: <FeedbackIcon />,
        },
        {
          path: '/user/settings',
          key: 'settings',
          label: 'Settings',
          icon: <SettingsIcon />,
        },
      ],
    },
  ];

  if (myApprovalsItem) {
    const assetsIdx = sections.findIndex((s) => s.title === 'Assets');
    if (assetsIdx !== -1) {
      sections[assetsIdx] = {
        ...sections[assetsIdx],
        items: [...sections[assetsIdx].items, myApprovalsItem],
      };
    }
  }

  return sections;
};
const UserSidebar = ({ logout, isOpen, isMobile, onSidebarToggle, currentUser }) => {
  const location = useLocation();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  // Pending approvals badge (universal — any authenticated user)
  const { count: pendingApprovalsCount, userIsApprover } =
    usePendingApprovalsCount(currentUser, location.pathname);

  const myApprovalsItem = userIsApprover
    ? {
        path: '/user/my-approvals',
        key: 'myApprovals',
        label: 'My Approvals',
        icon: <HowToRegIcon />,
        badgeCount: pendingApprovalsCount,
      }
    : null;

  const NAV_SECTIONS = buildNavSections(myApprovalsItem);

  const getActiveKey = () => {
    const allItems = NAV_SECTIONS.flatMap((s) => s.items);
    const matched = allItems.find((item) =>
      location.pathname.startsWith(item.path)
    );
    return matched ? matched.key : '';
  };
  const activeKey = getActiveKey();
  const currentWidth = isOpen ? SIDEBAR_WIDTH_EXPANDED : SIDEBAR_WIDTH_COLLAPSED;

  const handleToggle = () => onSidebarToggle(!isOpen);
  const handleNavClick = () => { if (isMobile) onSidebarToggle(false); };
  const handleLogoutClick = () => setShowLogoutModal(true);
  const handleLogoutConfirm = async () => {
    setLogoutLoading(true);
    try { await logout(); } catch (e) { console.error(e); }
    finally { setLogoutLoading(false); setShowLogoutModal(false); }
  };

  const drawerContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        background: 'linear-gradient(180deg, #ffffff 0%, #fafbfc 100%)',
      }}
    >
      {/* ── HEADER ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isOpen ? 'space-between' : 'center',
          gap: 1,
          px: isOpen ? 1.5 : 1,
          py: 1.5,
          minHeight: 88,
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: alpha('#000', 0.06),
          overflow: 'hidden',
        }}
      >
        {isOpen ? (
          <>
            {/* Logo */}
            <Box
              component={Link}
              to="/user/dashboard"
              sx={{
                flex: 1,
                minWidth: 0,
                height: 64,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                overflow: 'hidden',
                textDecoration: 'none',
                cursor: 'pointer',
                borderRadius: 1,
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: alpha(BRAND_PRIMARY, 0.04),
                },
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="Logo"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/default-logo.png';
                }}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  width: 'auto',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>

            {/* Collapse button */}
            <IconButton
              onClick={handleToggle}
              size="small"
              sx={{
                flexShrink: 0,
                color: 'text.secondary',
                '&:hover': {
                  backgroundColor: alpha(BRAND_PRIMARY, 0.08),
                  color: BRAND_PRIMARY,
                },
              }}
            >
              <MenuOpenIcon />
            </IconButton>
          </>
        ) : (
          <IconButton
            onClick={handleToggle}
            sx={{
              color: BRAND_PRIMARY,
              backgroundColor: BRAND_LIGHT,
              '&:hover': { backgroundColor: alpha(BRAND_PRIMARY, 0.15) },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* ── NAV ── */}
      <Box
        component="nav"
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 1.5,
          px: 1.25,
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: alpha('#000', 0.12),
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
        }}
      >
        {NAV_SECTIONS.map((section) => (
          <Box key={section.title} sx={{ mb: 2 }}>
            {isOpen && (
              <Typography
                variant="caption"
                sx={{
                  px: 2,
                  py: 0.5,
                  display: 'block',
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  opacity: 0.7,
                }}
              >
                {section.title}
              </Typography>
            )}

            <List disablePadding>
                           {section.items.map((item) => {
                 const { path, key, label, icon, badgeCount } = item;
                const isActive = activeKey === key;
                return (
                  <ListItem key={key} disablePadding sx={{ mb: 0.25 }}>
                    <Tooltip title={!isOpen ? label : ''} placement="right" arrow>
                      <ListItemButton
                        component={Link}
                        to={path}
                        onClick={handleNavClick}
                        sx={{
                          minHeight: 44,
                          borderRadius: '10px',
                          justifyContent: isOpen ? 'flex-start' : 'center',
                          px: isOpen ? 1.5 : 0,
                          mx: isOpen ? 0 : 'auto',
                          width: isOpen ? '100%' : 48,
                          position: 'relative',
                          backgroundColor: isActive ? BRAND_LIGHT : 'transparent',
                          color: isActive ? BRAND_PRIMARY : 'text.primary',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            backgroundColor: isActive
                              ? BRAND_LIGHT
                              : alpha('#000', 0.04),
                          },
                          '&::before': isActive
                            ? {
                                content: '""',
                                position: 'absolute',
                                left: isOpen ? -6 : -12,
                                top: '20%',
                                bottom: '20%',
                                width: 3,
                                borderRadius: '0 4px 4px 0',
                                backgroundColor: BRAND_PRIMARY,
                              }
                            : {},
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: isOpen ? 1.75 : 0,
                            justifyContent: 'center',
                            color: isActive ? BRAND_PRIMARY : 'text.secondary',
                            '& svg': { fontSize: '1.35rem' },
                          }}
                        >
                          {icon}
                        </ListItemIcon>
                                             {isOpen && (
                          <ListItemText
                            primary={label}
                            primaryTypographyProps={{
                              fontSize: '0.875rem',
                              fontWeight: isActive ? 600 : 500,
                              noWrap: true,
                            }}
                          />
                        )}

                        {/* Badge chip (expanded sidebar) */}
                        {badgeCount > 0 && isOpen && (
                          <Box
                            sx={{
                              ml: 1,
                              minWidth: 20,
                              height: 20,
                              px: 0.75,
                              borderRadius: 10,
                              bgcolor: isActive ? BRAND_PRIMARY : '#dc2626',
                              color: '#fff',
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            {badgeCount > 99 ? '99+' : badgeCount}
                          </Box>
                        )}

                        {/* Red dot (collapsed sidebar) */}
                        {badgeCount > 0 && !isOpen && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 6,
                              right: 6,
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: '#dc2626',
                            }}
                          />
                        )}
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                );
              })}
            </List>
          </Box>
        ))}
      </Box>

      {/* ── FOOTER / LOGOUT ── */}
      <Box
        sx={{
          p: 1.25,
          flexShrink: 0,
          borderTop: '1px solid',
          borderColor: alpha('#000', 0.06),
        }}
      >
        <Tooltip title={!isOpen ? 'Logout' : ''} placement="right" arrow>
          <ListItemButton
            onClick={handleLogoutClick}
            sx={{
              minHeight: 44,
              borderRadius: '10px',
              justifyContent: isOpen ? 'flex-start' : 'center',
              px: isOpen ? 1.5 : 0,
              mx: isOpen ? 0 : 'auto',
              width: isOpen ? '100%' : 48,
              color: '#dc2626',
              '&:hover': { backgroundColor: alpha('#dc2626', 0.08) },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isOpen ? 1.75 : 0,
                justifyContent: 'center',
                color: '#dc2626',
                '& svg': { fontSize: '1.35rem' },
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            {isOpen && (
              <ListItemText
                primary="Logout"
                primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 600 }}
              />
            )}
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Mobile FAB — shown only when collapsed on mobile */}
      {isMobile && !isOpen && (
        <Fab
          size="medium"
          onClick={handleToggle}
          aria-label="Open sidebar"
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1300,
            backgroundColor: BRAND_PRIMARY,
            color: '#fff',
            boxShadow: '0 4px 12px rgba(30, 64, 175, 0.3)',
            '&:hover': { backgroundColor: '#1e3a8a' },
          }}
        >
          <MenuIcon />
        </Fab>
      )}

      {/* Desktop — permanent collapsible drawer */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          open={isOpen}
          PaperProps={{
            sx: {
              width: currentWidth,
              transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
              border: 'none',
              boxShadow: '0 0 20px rgba(0,0,0,0.04)',
              boxSizing: 'border-box',
            },
          }}
          sx={{
            width: currentWidth,
            flexShrink: 0,
            transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            '& .MuiDrawer-paper': {
              width: currentWidth,
              boxSizing: 'border-box',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Mobile — temporary overlay drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={isOpen}
          onClose={() => onSidebarToggle(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: SIDEBAR_WIDTH_EXPANDED,
              boxSizing: 'border-box',
              border: 'none',
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {showLogoutModal && (
        <LogoutConfirmation
          onClose={() => setShowLogoutModal(false)}
          onConfirm={handleLogoutConfirm}
          loading={logoutLoading}
        />
      )}
    </>
  );
};

export default UserSidebar;